import {
  ActivityTimeline,
  Button,
  type CursorPage,
  DetailList,
  fieldErrorsOf,
  formatDateTime,
  formatMoney,
  ListView,
  PageHeader,
  RecordDrawer,
  TextButton,
  useCursorList,
  useIdempotencyKeys,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { FilterToolbar } from "@majitap/ui/arc/filter-toolbar/filter-toolbar";
import { Input } from "@majitap/ui/arc/input/input";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { type FormEvent, type ReactNode, useState } from "react";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import { filterSearch } from "../../list-search";

/** Development only (/dev/kit): every piece of the list, record and form kit with made-up data. */
export const kitFilters = filterSearch({ status: ["open", "delivered", "cancelled"], q: "text" });

type Row = { id: string; number: string; customer: string; total: string; status: string; created_at: string };
const STATUSES = ["open", "delivered", "cancelled"] as const;
const ALL: Row[] = Array.from({ length: 23 }, (_, i) => ({
  id: `o-${i + 1}`,
  number: `ORD-${1000 + i}`,
  customer: ["Amina Otieno", "Juma Wanjiru", "Hotel Savannah", "Kilimani Flats"][i % 4],
  total: `${(i + 1) * 1250}.${i % 2 ? "50" : "00"}`,
  status: STATUSES[i % 3],
  created_at: new Date(Date.UTC(2026, 9, 18, 6 + (i % 10), i)).toISOString(),
}));
const TONE: Record<string, "info" | "success" | "neutral"> = {
  open: "info",
  delivered: "success",
  cancelled: "neutral",
};

/** A fake list endpoint: 10 a page, filtered like the real ones. */
async function fakePage(status: string | undefined, cursor: string | undefined): Promise<CursorPage<Row>> {
  await new Promise((r) => setTimeout(r, 300));
  const rows = ALL.filter((r) => !status || r.status === status);
  const start = cursor ? rows.findIndex((r) => r.id === cursor) + 1 : 0;
  const data = rows.slice(start, start + 10);
  return { data, next_cursor: start + 10 < rows.length ? (data.at(-1)?.id ?? null) : null };
}

export function KitPage(): ReactNode {
  const search = useSearch({ from: "/console/dev/kit" });
  const navigate = useNavigate({ from: "/dev/kit" });
  const format = useCompanyFormat();
  const [open, setOpen] = useState<Row | null>(null);
  const list = useCursorList({
    queryKey: ["kit", search.status],
    fetchPage: (cursor) => fakePage(search.status, cursor),
  });
  const active = kitFilters.active(search);

  return (
    <>
      <PageHeader
        title="Kit"
        description="Every list, record and form piece, with made-up data. Development builds only."
        actions={<Button>New order</Button>}
      />
      <ListView
        caption="Orders"
        itemName={{ one: "order", other: "orders" }}
        rows={list.rows}
        rowKey={(r) => r.id}
        status={list.status}
        errorMessage={messageFor(null)}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={active.length > 0}
        onResetFilters={() => navigate({ search: {} })}
        empty={{ title: "No orders yet", description: "Orders placed by customers or staff show here." }}
        toolbar={
          <>
            <SegmentedControl
              label="Status"
              value={search.status ?? "all"}
              onValueChange={(v) =>
                navigate({
                  search: (s) => ({ ...s, status: v === "all" ? undefined : (v as (typeof STATUSES)[number]) }),
                })
              }
              options={[
                { value: "all", label: "All" },
                ...STATUSES.map((s) => ({ value: s, label: s[0].toUpperCase() + s.slice(1) })),
              ]}
            />
            <FilterToolbar
              filters={active.map(([id, value]) => ({ id, label: id, value }))}
              onRemove={(id) => navigate({ search: (s) => ({ ...s, [id]: undefined }) })}
              onClearAll={() => navigate({ search: {} })}
            />
          </>
        }
        columns={[
          {
            key: "number",
            label: "Order",
            render: (r) => <TextButton onClick={() => setOpen(r)}>{r.number}</TextButton>,
          },
          { key: "customer", label: "Customer" },
          { key: "total", label: "Total", numeric: true, render: (r) => formatMoney(r.total, format) },
          { key: "status", label: "Status", render: (r) => <Badge tone={TONE[r.status]}>{r.status}</Badge> },
          { key: "created_at", label: "Created", render: (r) => formatDateTime(r.created_at, format) },
        ]}
      />
      <RecordDrawer
        open={open !== null}
        onOpenChange={(o) => !o && setOpen(null)}
        title={open?.number ?? ""}
        description={open?.customer}
      >
        {open ? (
          <>
            <DetailList
              items={[
                { label: "Customer", value: open.customer },
                { label: "Total", value: formatMoney(open.total, format) },
                { label: "Status", value: <Badge tone={TONE[open.status]}>{open.status}</Badge> },
                { label: "Created", value: formatDateTime(open.created_at, format) },
                { label: "Voucher", value: null },
              ]}
            />
            <ActivityTimeline
              timeZone={format.timezone}
              entries={[
                { id: "a1", verb: "created", target_type: "order", created_at: open.created_at },
                {
                  id: "a2",
                  verb: "updated",
                  target_type: "order",
                  created_at: new Date(Date.parse(open.created_at) + 3_600_000).toISOString(),
                  diff: { status: { from: "open", to: open.status } },
                },
              ]}
            />
          </>
        ) : null}
      </RecordDrawer>
      <KitForm />
    </>
  );
}

/** A form that the "API" refuses once with field errors, then accepts: errors under fields, the key reused on retry. */
function KitForm() {
  const keys = useIdempotencyKeys();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState<string>();
  const [sent, setSent] = useState<string[]>([]);
  const [pending, setPending] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const key = keys.keyFor({ name, phone });
    setPending(true);
    await new Promise((r) => setTimeout(r, 300));
    setSent((s) => [...s, key.slice(0, 8)]);
    if (!/^\+\d{9,14}$/.test(phone)) {
      const error = {
        code: "VALIDATION_FAILED",
        message: "The request is invalid.",
        details: { issues: [{ path: "phone_number", message: "Use the international format, e.g. +254712345678." }] },
      };
      setErrors(fieldErrorsOf(error));
      setSummary(messageFor(error));
    } else {
      keys.settled();
      setErrors({});
      setSummary(undefined);
    }
    setPending(false);
  };

  return (
    <form onSubmit={submit} noValidate style={{ display: "grid", gap: "var(--space-4)", maxWidth: "28rem" }}>
      <h2 style={{ margin: 0, fontSize: "var(--text-lg)", fontWeight: 500 }}>Form</h2>
      {summary ? <Alert tone="danger" title={summary} /> : null}
      <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} error={errors.name} />
      <Input
        label="Phone number"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        error={errors.phone_number}
      />
      <Button type="submit" loading={pending}>
        Add customer
      </Button>
      <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: "var(--text-xs)" }}>
        Idempotency keys sent: {sent.join(", ") || "none"}
      </p>
    </form>
  );
}
